<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'
import type {
  CalcSubject,
  GuestTesterInput,
  MemberCompletionState,
  TestSessionDraft,
  UFPMMember,
  UFPMScoreEntry,
} from '../lib/types'
import { useUFPMTestSession } from '../composables/useUFPMTestSession'
import { guestJerseyMeta, guestLabel, isGuestId } from '../lib/testSession'
import UFPMCalcPanel from './UFPMCalcPanel.vue'
import UFPMCalcMemberPicker from './UFPMCalcMemberPicker.vue'
import UFPMAddTesterPanel from './UFPMAddTesterPanel.vue'
import UFPMPlansPanel from './UFPMPlansPanel.vue'
import { scoreEntryToResult } from '../lib/scoreEntryToResult'
import { scoreBandClass } from '../lib/scoreDisplay'
import type { ScoreResult } from '@/features/scoring/lib/types'

type Section = 'test' | 'calc' | 'plans'
const section = ref<Section>('test')

const members = shallowRef<readonly UFPMMember[]>([])

function refreshMembers(): void {
  members.value = window.ufpmTools?.getMembers?.() ?? []
}
function onMembersChanged(event: Event): void {
  const next = (event as CustomEvent).detail?.members as UFPMMember[] | undefined
  if (next) members.value = next
}

onMounted(() => { refreshMembers(); window.addEventListener('ptr:ufpm-members-changed', onMembersChanged) })
onUnmounted(() => { window.removeEventListener('ptr:ufpm-members-changed', onMembersChanged) })

const {
  session,
  addMember,
  addGuest,
  removeMember,
  setActive,
  updateDraft,
  clearDraft,
  markSaved,
  markGuestSaved,
  clearSessionWithConfirm,
  savedCount,
  draftCount,
} = useUFPMTestSession()

const showAddPanel = ref(false)

const guests = computed(() => session.value.guests ?? {})

// ---- queue entries: roster members and guests unified for display ----

interface QueueEntry {
  id: string
  kind: 'member' | 'guest'
  label: string
  meta: string
  completion: MemberCompletionState
}

function memberLabel(m: UFPMMember | null): string {
  if (!m) return 'Unknown Member'
  return [m.rank, m.fullName].filter(Boolean).join(' ') || 'Unknown'
}

const queueEntries = computed<QueueEntry[]>(() =>
  session.value.memberIds.map((id): QueueEntry => {
    const completion = session.value.members[id]?.completion ?? 'empty'
    if (isGuestId(id)) {
      const g = guests.value[id]
      return {
        id,
        kind: 'guest',
        label: g ? guestLabel(g) : 'Unknown Tester',
        meta: g ? guestJerseyMeta(g) : '',
        completion,
      }
    }
    const m = members.value.find(mm => mm.id === id) ?? null
    return { id, kind: 'member', label: memberLabel(m), meta: m?.organization ?? '', completion }
  })
)

// ---- active subject (member | guest | hypothetical) ----

const HYPOTHETICAL_SUBJECT: CalcSubject = { kind: 'hypothetical' }

// UI-level override: chosen via the picker's "Hypothetical — no member" option.
// Deliberately NOT persisted to the session so drafts are untouched.
const hypotheticalChosen = ref(false)

const activeSubject = computed<CalcSubject>(() => {
  if (hypotheticalChosen.value) return HYPOTHETICAL_SUBJECT
  const id = session.value.activeMemberId
  if (!id) return HYPOTHETICAL_SUBJECT
  if (isGuestId(id)) {
    const g = guests.value[id]
    return g ? { kind: 'guest', guest: g } : HYPOTHETICAL_SUBJECT
  }
  const m = members.value.find(mm => mm.id === id)
  return m ? { kind: 'member', member: m } : HYPOTHETICAL_SUBJECT
})

const activeSubjectId = computed<string | null>(() => {
  const s = activeSubject.value
  if (s.kind === 'member') return s.member.id
  if (s.kind === 'guest') return s.guest.id
  return null
})

const activeLabel = computed(() => {
  const s = activeSubject.value
  if (s.kind === 'member') return memberLabel(s.member)
  if (s.kind === 'guest') return guestLabel(s.guest)
  return ''
})

const activeDraft = computed(() => {
  const id = activeSubjectId.value
  if (!id) return undefined
  return session.value.members[id]?.draft
})

const activeCompletion = computed<MemberCompletionState>(() => {
  const id = activeSubjectId.value
  if (!id) return 'empty'
  return session.value.members[id]?.completion ?? 'empty'
})

const activeGuestSavedScore = computed<UFPMScoreEntry | undefined>(() => {
  const id = activeSubjectId.value
  if (!id || !isGuestId(id)) return undefined
  // session is deeply readonly (via Vue's readonly()); cast back to the mutable
  // UFPMScoreEntry shape expected downstream — this is a type-only distinction,
  // not a runtime one.
  return session.value.members[id]?.savedScore as UFPMScoreEntry | undefined
})

function entrySexToProfile(sex: string | undefined): 'male' | 'female' | '' {
  return sex === 'male' || sex === 'female' ? sex : ''
}

const activeSavedScore = computed(() => {
  const s = activeSubject.value
  if (activeCompletion.value !== 'saved') return null
  if (s.kind === 'member') {
    const entry = s.member.history?.[0]
    return entry ? scoreEntryToResult(entry, s.member) : null
  }
  if (s.kind === 'guest') {
    const entry = activeGuestSavedScore.value
    return entry
      ? scoreEntryToResult(entry, { age: Number(entry.age || 0), sex: entrySexToProfile(entry.sex) })
      : null
  }
  return null
})

// ---- actions ----

function activateQueueMember(testerId: string): void {
  hypotheticalChosen.value = false
  setActive(testerId)
  if (!isGuestId(testerId)) window.ufpmTools?.selectMember?.(testerId)
}

function chooseHypothetical(): void {
  hypotheticalChosen.value = true
}

function doAddMember(m: UFPMMember): void {
  hypotheticalChosen.value = false
  addMember(m.id)
  window.ufpmTools?.selectMember?.(m.id)
  showAddPanel.value = false
}

function doAddGuest(input: GuestTesterInput): void {
  hypotheticalChosen.value = false
  addGuest(input) // addGuest makes the new guest the active tester
  showAddPanel.value = false
}

function goToCalc(): void {
  section.value = 'calc'
  ensureCalcMemberSelected()
}

function ensureCalcMemberSelected(): void {
  if (hypotheticalChosen.value) return
  if (!session.value.memberIds.length) return
  if (session.value.activeMemberId && session.value.memberIds.includes(session.value.activeMemberId)) return
  const firstId = session.value.memberIds[0]
  if (firstId) activateQueueMember(firstId)
}

watch(section, (tab) => {
  if (tab === 'calc') ensureCalcMemberSelected()
})

function handleSaved(subjectId: string, _score: ScoreResult, entry: UFPMScoreEntry): void {
  if (isGuestId(subjectId)) markGuestSaved(subjectId, entry)
  else markSaved(subjectId)
}

function handleDraftUpdate(subjectId: string, draft: TestSessionDraft): void {
  updateDraft(subjectId, draft)
}

function handleDraftClear(subjectId: string): void {
  clearDraft(subjectId)
}

function confirmRemoveTester(entry: QueueEntry): void {
  const note = entry.kind === 'guest'
    ? 'Their session score will be discarded.'
    : 'Saved scores stay in unit history.'
  if (!window.confirm(`Remove ${entry.label} from active testers? ${note}`)) return
  removeMember(entry.id)
  const nextId = session.value.activeMemberId
  if (nextId && !isGuestId(nextId)) window.ufpmTools?.selectMember?.(nextId)
}

// ---- display helpers ----

const sessionCompletionById = computed(() => {
  const out: Record<string, MemberCompletionState> = {}
  for (const id of session.value.memberIds) {
    out[id] = session.value.members[id]?.completion ?? 'empty'
  }
  return out
})

const planMemberIds = computed(() => session.value.memberIds.filter(id => !isGuestId(id)))

function completionIcon(c: MemberCompletionState): string {
  if (c === 'saved') return '●'
  if (c === 'draft') return '◐'
  return '○'
}

function completionClass(c: MemberCompletionState): string {
  if (c === 'saved') return 'session-state--saved'
  if (c === 'draft') return 'session-state--draft'
  return 'session-state--empty'
}

function completionLabel(c: MemberCompletionState): string {
  if (c === 'saved') return 'Score saved'
  if (c === 'draft') return 'Draft in progress'
  return 'Not started'
}
</script>

<template>
  <div class="ufpm-testing-tab">

    <div class="ufpm-section-tabs" role="tablist">
      <button
        class="ufpm-section-tab"
        :class="{ active: section === 'test' }"
        type="button"
        role="tab"
        data-onboarding="ufpm-testing-session"
        @click="section = 'test'"
      >Test</button>
      <button
        class="ufpm-section-tab"
        :class="{ active: section === 'calc' }"
        type="button"
        role="tab"
        data-onboarding="ufpm-testing-calc"
        @click="section = 'calc'"
      >Calc</button>
      <button
        class="ufpm-section-tab"
        :class="{ active: section === 'plans' }"
        type="button"
        role="tab"
        @click="section = 'plans'"
      >Plans</button>
    </div>

    <!-- TEST: session queue only -->
    <div v-show="section === 'test'" class="ufpm-test-section">
      <div class="ufpm-session-bar">
        <span class="ufpm-session-counts">
          <span class="session-state--saved">{{ savedCount }} saved</span>
          <span v-if="draftCount" class="session-state--draft"> · {{ draftCount }} draft</span>
          <span> · {{ session.memberIds.length }} total</span>
        </span>
        <button
          class="btn btn-secondary ufpm-session-action"
          type="button"
          :disabled="!session.memberIds.length"
          @click="clearSessionWithConfirm()"
        >Clear Session</button>
      </div>

      <div class="ufpm-queue">
        <div
          v-for="entry in queueEntries"
          :key="entry.id"
          class="ufpm-queue-row"
          :class="{ 'ufpm-queue-row--active': entry.id === activeSubjectId }"
          role="button"
          tabindex="0"
          @click="activateQueueMember(entry.id)"
          @keydown.enter="activateQueueMember(entry.id)"
        >
          <span class="ufpm-queue-state" :class="completionClass(entry.completion)">{{ completionIcon(entry.completion) }}</span>
          <span class="ufpm-queue-name">{{ entry.label }}</span>
          <span v-if="entry.meta" class="ufpm-queue-org">{{ entry.meta }}</span>
          <button
            v-if="entry.id === activeSubjectId"
            class="ufpm-queue-remove"
            type="button"
            aria-label="Remove from active testers"
            @click.stop="confirmRemoveTester(entry)"
          >✕</button>
        </div>

        <button
          class="btn btn-secondary ufpm-queue-add-btn"
          type="button"
          @click="showAddPanel = !showAddPanel"
        >+ Add Member</button>
      </div>

      <div v-if="showAddPanel" class="ufpm-add-panel">
        <UFPMAddTesterPanel
          :members="members"
          :session-member-ids="session.memberIds"
          @add-member="doAddMember"
          @add-guest="doAddGuest"
        />
      </div>

      <div v-if="activeSubject.kind !== 'hypothetical'" class="card ufpm-test-summary">
        <div class="card-title">Selected member</div>
        <div class="ufpm-test-summary__name">{{ activeLabel }}</div>
        <div class="ufpm-test-summary__status" :class="completionClass(activeCompletion)">
          {{ completionIcon(activeCompletion) }} {{ completionLabel(activeCompletion) }}
        </div>
        <p class="ufpm-test-summary__hint">
          Open the <button class="ufpm-inline-tab-link" type="button" @click="goToCalc">Calc</button> tab to enter component scores.
        </p>
        <div v-if="activeSavedScore" class="ufpm-test-summary__saved">
          <span
            class="ufpm-test-summary__score"
            :class="scoreBandClass(activeSavedScore.cat, activeSavedScore.composite)"
          >{{ activeSavedScore.composite.toFixed(1) }}</span>
          <span
            class="ufpm-test-summary__cat"
            :class="scoreBandClass(activeSavedScore.cat, activeSavedScore.composite)"
          >{{ activeSavedScore.cat }}</span>
        </div>
      </div>
      <div v-else-if="!session.memberIds.length" class="ufpm-empty" style="padding: 24px 16px;">
        Add members above to start a test session, or open the Calc tab to run a hypothetical score.
      </div>
      <div v-else class="ufpm-empty" style="padding: 24px 16px;">
        Select a member from the queue, then switch to Calc to score.
      </div>
    </div>

    <!-- CALC: inline score entry -->
    <div v-show="section === 'calc'" class="ufpm-calc-section">
      <UFPMCalcMemberPicker
        :members="members"
        :guests="guests"
        :session-member-ids="session.memberIds"
        :active-member-id="session.activeMemberId"
        :hypothetical-active="activeSubject.kind === 'hypothetical'"
        :completion-by-id="sessionCompletionById"
        @select="activateQueueMember"
        @hypothetical="chooseHypothetical"
        @add-member="doAddMember"
        @add-guest="doAddGuest"
      />
      <UFPMCalcPanel
        v-if="activeSubject.kind !== 'hypothetical'"
        :key="activeSubjectId ?? 'none'"
        :subject="activeSubject"
        :draft="activeDraft"
        :completion="activeCompletion"
        :saved-score="activeGuestSavedScore"
        @update-draft="handleDraftUpdate"
        @clear-draft="handleDraftClear"
        @saved="handleSaved"
      />
      <!-- Hypothetical panel stays mounted (v-show) so what-if inputs survive tab switches -->
      <UFPMCalcPanel
        v-show="activeSubject.kind === 'hypothetical'"
        :subject="HYPOTHETICAL_SUBJECT"
      />
    </div>

    <!-- PLANS (roster members only — guests have no history to plan from) -->
    <div v-show="section === 'plans'" class="ufpm-plans-section">
      <UFPMPlansPanel :session-member-ids="planMemberIds" />
    </div>

  </div>
</template>
